import { useCallback } from "react";
import {
  Card,
  BlockStack,
  Text,
  TextField,
  FormLayout,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { PhoneNumberField } from "@shared/ui";
import { CityField } from "@shared/ui";
import { Instructor } from "@features/events/types";
import { GeoAddress } from "@features/events/types";
import { parsePhoneNumberWithError } from "libphonenumber-js";

interface ContactInformationCardProps {
  instructorData: Partial<Instructor>;
  onDataChange: (updates: Partial<Instructor>) => void;
  fieldErrors: Record<string, string | undefined>;
}

export default function ContactInformationCard({
  instructorData,
  onDataChange,
  fieldErrors,
}: ContactInformationCardProps) {
  const { t } = useTranslation();

  const handleEmailChange = useCallback(
    (value: string) => {
      onDataChange({ email: value });
    },
    [onDataChange]
  );

  const handlePhoneChange = useCallback(
    (value: string) => {
      try {
        const phoneObj = parsePhoneNumberWithError(value);
        if (phoneObj) {
          const cc = `+${phoneObj.countryCallingCode}`;
          const ln = phoneObj.nationalNumber;
          onDataChange({
            phoneCountryCode: cc,
            phone: ln,
          });
        }
      } catch (err) {
        console.log(err);
      }
    },
    [onDataChange]
  );

  const handleCityChange = useCallback(
    (geoAddress: GeoAddress | null) => {
      onDataChange({ geoAddress: geoAddress || undefined });
    },
    [onDataChange]
  );

  // Combine phone country code and phone number for PhoneNumberField
  const fullPhoneNumber = `${instructorData.phoneCountryCode || "+359"}${
    instructorData.phone || ""
  }`;

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2">
          {t("instructorDetails.contactInformation.title")}
        </Text>
        <Text as="p" variant="bodyMd" tone="subdued">
          {t("instructorDetails.contactInformation.subtitle")}
        </Text>

        <FormLayout>
          <PhoneNumberField
            phoneLabel={t("instructorDetails.contactInformation.phone")}
            value={fullPhoneNumber}
            onChange={handlePhoneChange}
            phoneError={fieldErrors.phone || fieldErrors.phoneCountryCode}
          />

          <TextField
            label={t("instructorDetails.contactInformation.email")}
            type="email"
            value={instructorData.email || ""}
            onChange={handleEmailChange}
            error={fieldErrors.email}
            autoComplete="email"
          />

          <CityField
            label={t("instructorDetails.contactInformation.primaryAddress")}
            value={instructorData.geoAddress || null}
            onChange={handleCityChange}
            error={fieldErrors.geoAddress}
            requiredIndicator
          />
        </FormLayout>
      </BlockStack>
    </Card>
  );
}
